import React from "react";
import classNames from "classnames";
import styles from "./Label.module.scss";

export interface LabelProps {
  value: string | number | Date;
  format?: string;
  color?: string; // Couleur du texte
  size?: string; // Taille de la police
  bold?: boolean; // Texte en gras
  italic?: boolean; // Texte en italique
  ariaLabel?: string; // Libellé ARIA
  ariaLive?: "off" | "assertive" | "polite"; // Type de notification ARIA
  ariaAtomic?: boolean; // Si le texte change, doit être mis à jour immédiatement
  width?: string; // Largeur du composant
  position?: "left" | "center" | "right"; // Alignement du texte
}

const Label: React.FC<LabelProps> = ({
  value,
  format = "DD/MM/YYYY",
  color = "var(--color-Label-FG)",
  size = "16px",
  bold = false,
  italic = false,
  ariaLabel,
  ariaLive = "polite",
  ariaAtomic = false,
  width, // Largeur personnalisée
  position = "left", // Alignement du texte
}) => {
  const getFormattedValue = (): string => {
    if (value instanceof Date) {
      const formatter = new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      return formatter.format(value);
    }

    if (typeof value === "number") {
      // Format number as integer
      return Math.round(value).toString();
    }

    return String(value);
  };

  const formattedValue = getFormattedValue();

  const labelClass = classNames(styles.label, {
    [styles.bold]: bold,
    [styles.italic]: italic,
  });

  const getTextAlign = (): React.CSSProperties => {
    switch (position) {
      case "left":
        return { textAlign: "left" };
      case "center":
        return { textAlign: "center" };
      case "right":
        return { textAlign: "right" };
      default:
        return { textAlign: "left" };
    }
  };

  const customStyle: React.CSSProperties = {
    color, // Couleur personnalisée
    fontSize: size, // Taille personnalisée
    width, // Largeur personnalisée
    ...getTextAlign(), // Alignement du texte
  };

  return (
    <span
      className={labelClass}
      style={customStyle}
      aria-label={ariaLabel}
      aria-live={ariaLive}
      aria-atomic={ariaAtomic}
    >
      {formattedValue}
    </span>
  );
};

export default Label;
